import { BACKEND_URL, AUTH0_SESSION_PROBE_FLAG } from 'configs'
import { logout } from 'tabs/authorization/actions/security'
import { expireQcsHandoffSession } from 'tabs/authorization/api/admin'
import { isQcsHandoffSession } from 'tabs/authorization/helpers/qcsHandoffSession'
import { showSnackBar } from 'tabs/home/actions/snackBar'

/**
 * Global handling of expired/unauthenticated sessions (HTTP 401).
 *
 * A 401 from our backend means the session is no longer valid, so the user must
 * be logged out. Historically this was the responsibility of every individual
 * api function (via `logoutOnRequestOrResponseJson`), but many callers only
 * invoked that helper on a 200 response — so a 401 silently did nothing.
 *
 * This module makes 401 handling a property of the system: a single `fetch`
 * wrapper detects every 401 and triggers the logout flow, regardless of how the
 * calling code handles (or ignores) the response body.
 */

// Pre-auth / auth endpoint segments (relative to the backend base path) where a
// 401 means "bad credentials" or "invalid/expired token" — NOT "your session
// expired". These must never trigger the global logout (and `logout` itself must
// be excluded to avoid a loop).
//
// Matched as the FIRST path segment after the backend base, so `/login` and
// `/login/page/element` are excluded, but an authenticated route that merely
// contains the word — e.g. `/users/management/resend/invite/{id}` or
// `/private/user/login-history` — is correctly NOT excluded.
const AUTH_EXCLUDED_SEGMENTS = [
  'login',
  'logout',
  'signUp',
  'invite',
  'resetPassword',
  'auth/verify-otp',
  'auth0',
]

// Guard so a burst of parallel 401s triggers a single logout, not one per call.
let isHandlingSessionExpiry = false

const resolveUrl = (url: string): URL | null => {
  try {
    return new URL(url, window.location.origin)
  } catch {
    return null
  }
}

const getRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  if (input instanceof Request) return input.url
  return String(input)
}

const isAuthenticatedBackendCall = (rawUrl: string): boolean => {
  const url = resolveUrl(rawUrl)
  const backend = resolveUrl(BACKEND_URL)
  if (!url || !backend) return false

  // Must target our backend: same origin and under its base path.
  if (url.origin !== backend.origin || !url.pathname.startsWith(backend.pathname)) {
    return false
  }

  // Path relative to the backend base, e.g. '/login' or '/private/facebook/22/all'.
  const relativePath = url.pathname.slice(backend.pathname.length)

  // Excluded only when an auth keyword is the first path segment.
  const isAuthEndpoint = AUTH_EXCLUDED_SEGMENTS.some(
    segment => relativePath === `/${segment}` || relativePath.startsWith(`/${segment}/`),
  )

  return !isAuthEndpoint
}

/**
 * Single, idempotent entry point for "the session has expired". Safe to call
 * from the fetch interceptor and from the legacy api helpers — concurrent calls
 * collapse into one logout.
 */
export const handleSessionExpired = (): void => {
  if (isHandlingSessionExpiry) return
  isHandlingSessionExpiry = true

  // Handoff users can only sign back in from QCS, not the admin login page.
  if (isQcsHandoffSession()) {
    expireQcsHandoffSession()
    return
  }

  // The backend has already dropped this session, so only client state is cleared.
  // POST /logout would carry the backend cookie other portals share and end their session.
  Promise.resolve()
    .then(() => logout())
    .catch(error => {
      // Even if clearing the client session fails, still notify the user below.
      console.error('Session expiry logout failed:', error)
    })
    .then(() =>
      showSnackBar({
        open: true,
        text: 'You’ve been logged out. Please log back in.',
        type: 'error',
      }),
    )
    .finally(() => {
      // Re-arm after the burst settles so a future session can be detected again
      // (e.g. the user logs back in without a full page reload).
      setTimeout(() => {
        isHandlingSessionExpiry = false
      }, 3000)
    })
}

/**
 * Wraps `window.fetch` once so EVERY 401 from our backend triggers a logout,
 * independent of how each api function handles the response. The wrapper never
 * reads the body, so callers still receive the untouched response.
 */
export const installFetchInterceptor = (): void => {
  if ((window as any).__fetchInterceptorInstalled) return
  ;(window as any).__fetchInterceptorInstalled = true

  const originalFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input as RequestInfo, init)

    try {
      if (response.status === 401 && isAuthenticatedBackendCall(getRequestUrl(input))) {
        // Soft Auth0 resume probe — cookie missing is expected; do not logout/snackbar.
        let probingAuth0Session = false
        try {
          probingAuth0Session = sessionStorage.getItem(AUTH0_SESSION_PROBE_FLAG) === '1'
        } catch {
          probingAuth0Session = false
        }
        if (!probingAuth0Session) {
          handleSessionExpired()
        }
      }
    } catch {
      // The interceptor must never break a request.
    }

    return response
  }
}
